import type { BuilderElement } from '@/utils/builder/types';
import { validateUniqueIds, validateAndFixIds } from '@/utils/builder/id-utils';

// Find an element by ID in the tree
export const findElementById = (
  elements: BuilderElement[],
  id: string
): BuilderElement | null => {
  for (const element of elements) {
    if (element.id === id) return element;
    if (element.children) {
      const found = findElementById(element.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Get the parent chain of an element
export const getElementParents = (
  elements: BuilderElement[],
  id: string
): BuilderElement[] => {
  const path: BuilderElement[] = [];

  function dfs(
    nodes: BuilderElement[],
    currentPath: BuilderElement[]
  ): boolean {
    for (const node of nodes) {
      const newPath = [...currentPath, node];
      if (node.id === id) {
        path.push(...currentPath);
        return true;
      }
      if (node.children && dfs(node.children, newPath)) return true;
    }
    return false;
  }

  dfs(elements, []);
  return path;
};

// Check if element has a selected child
export const hasSelectedChild = (
  element: BuilderElement,
  selectedId: string
): boolean => {
  if (!element.children?.length) return false;
  for (const child of element.children) {
    if (child.id === selectedId) return true;
    if (hasSelectedChild(child, selectedId)) return true;
  }
  return false;
};

// Move element to a different container
export const moveElement = (
  elements: BuilderElement[],
  elementId: string,
  targetContainerId: string,
  targetIndex?: number
): BuilderElement[] => {
  let elementToMove: BuilderElement | null = null;

  const removeFromLocation = (els: BuilderElement[]): BuilderElement[] => {
    return els
      .map((element) => {
        if (element.id === elementId) {
          elementToMove = { ...element };
          return null;
        }
        if (element.children) {
          const updatedChildren = removeFromLocation(element.children);
          if (updatedChildren !== element.children) {
            return { ...element, children: updatedChildren };
          }
        }
        return element;
      })
      .filter(Boolean) as BuilderElement[];
  };

  const addToLocation = (els: BuilderElement[]): BuilderElement[] => {
    if (targetContainerId === 'root') {
      const newElements = [...els];
      if (elementToMove) {
        if (targetIndex !== undefined) {
          newElements.splice(targetIndex, 0, elementToMove);
        } else {
          newElements.push(elementToMove);
        }
      }
      return newElements;
    }

    return els.map((element) => {
      if (element.id === targetContainerId) {
        const children = element.children || [];
        const newChildren = [...children];
        if (elementToMove) {
          if (targetIndex !== undefined) {
            newChildren.splice(targetIndex, 0, elementToMove);
          } else {
            newChildren.push(elementToMove);
          }
        }
        return { ...element, children: newChildren };
      }
      if (element.children) {
        const updatedChildren = addToLocation(element.children);
        if (updatedChildren !== element.children) {
          return { ...element, children: updatedChildren };
        }
      }
      return element;
    });
  };

  const elementsAfterRemoval = removeFromLocation(elements);
  if (!elementToMove) {
    console.warn('⚠️ Element to move not found:', elementId);
    return elements;
  }
  return addToLocation(elementsAfterRemoval);
};

// Reorder elements within the same container
export const reorderElements = (
  elements: BuilderElement[],
  containerId: string,
  fromIndex: number,
  toIndex: number
): BuilderElement[] => {
  try {
    validateUniqueIds(elements);
  } catch (error) {
    console.warn('⚠️ Input elements have duplicates:', error);
  }

  let result: BuilderElement[];

  if (containerId === 'root') {
    const newElements = [...elements];
    const [movedElement] = newElements.splice(fromIndex, 1);
    newElements.splice(toIndex, 0, movedElement);
    result = newElements;
  } else {
    result = elements.map((element) => {
      if (element.id === containerId) {
        const children = element.children || [];
        const newChildren = [...children];
        const [movedElement] = newChildren.splice(fromIndex, 1);
        newChildren.splice(toIndex, 0, movedElement);
        return { ...element, children: newChildren };
      }
      if (element.children) {
        const updatedChildren = reorderElements(
          element.children,
          containerId,
          fromIndex,
          toIndex
        );
        if (updatedChildren !== element.children) {
          return { ...element, children: updatedChildren };
        }
      }
      return element;
    });
  }

  try {
    validateUniqueIds(result);
    return result;
  } catch {
    return validateAndFixIds(result);
  }
};
